import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useBrainStore } from '@/state/brainStore';
import { useSessionStore } from '@/state/sessionStore';
import { useSimulationStore } from '@/state/simulationStore';
import InfoTip from '@/components/InfoTip';
import { aud } from '@/utils/format';
import {
  reconcileDay,
  makeSaleLane,
  reconciliationDaysFor,
  type MakeSaleRow,
  type BankStage,
  type ReconExceptionKind,
} from '@/domain/reconciliationFlow';
import { reconcileDeposits, type DepositReconRow, type DepositReconState } from '@/domain/depositReconFlow';
import {
  resolveReconRow,
  approveReconResolution,
  rejectReconResolution,
  classifyResolvedRow,
  reconApprovalBlock,
  partialTopUps,
  type ReconResolution,
  type ReconAttachment,
  type PartialTopUp,
  type ApprovalBlockReason,
} from '@/domain/reconResolutionFlow';
import type { DepositChannel, Persona, StudioId } from '@/domain/types';

/**
 * Reconcile (amendment 11). ONE back-office screen for the two reconciliations the
 * studio actually runs: make-sale (system make-sale vs terminal docket vs bank
 * lump) and deposit (system deposit vs bank landing). Settlement is folded in as a
 * per-line BANK STAGE, not a separate screen. Exceptions-first: every line
 * auto-matches; you only work the handful that did not. A day filter scopes the
 * view for the close; a date column keeps every line placed. Reads the store only —
 * it never re-runs the commission maths. Provider-agnostic (no card brand appears).
 */

type Lane = 'makesale' | 'deposit';

const EXCEPTION_LABEL: Record<ReconExceptionKind, string> = {
  'missing-docket': 'Missing docket',
  'amount-mismatch': 'Amount mismatch',
  'name-missing': 'Name missing',
};

const BANK_STAGE_META: Record<BankStage, { label: string; dot: string; text: string }> = {
  landed: { label: 'Landed in lump', dot: 'bg-mint', text: 'text-mint' },
  awaiting: { label: 'Awaiting bank', dot: 'bg-azure', text: 'text-azure' },
  overdue: { label: 'Overdue · no echo', dot: 'bg-blood', text: 'text-blood' },
  till: { label: 'Cash · in till', dot: 'bg-cream-soft', text: 'text-cream-soft' },
};

const DEPOSIT_STATE_META: Record<DepositReconState, { label: string; dot: string; text: string }> = {
  matched: { label: 'Matched', dot: 'bg-mint', text: 'text-mint' },
  awaiting: { label: 'Awaiting bank', dot: 'bg-azure', text: 'text-azure' },
  'in-system-not-bank': { label: 'In the system, not the bank', dot: 'bg-blood', text: 'text-blood' },
  'in-bank-not-system': { label: 'In the bank, not the system', dot: 'bg-blood', text: 'text-blood' },
};

const CHANNEL_LABEL: Record<DepositChannel, string> = {
  card: 'Card',
  transfer: 'Transfer',
  cash: 'Cash',
  link: 'Link',
};

/** "Fri 1 May" for a yyyy-MM-dd (noon-UTC anchored so the weekday is right). */
const dayLabel = (dayIso: string): string => format(new Date(`${dayIso}T12:00:00Z`), 'EEE d MMM');

export default function Reconciliation() {
  const studios = useBrainStore((s) => s.studios);
  const salePayments = useBrainStore((s) => s.salePayments);
  const docketLines = useBrainStore((s) => s.docketLines);
  const settlementLumps = useBrainStore((s) => s.settlementLumps);
  const deposits = useBrainStore((s) => s.deposits);
  const depositLandings = useBrainStore((s) => s.depositLandings);
  const cashLedgers = useBrainStore((s) => s.cashLedgers);
  const reconResolutions = useBrainStore((s) => s.reconResolutions);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const assignedStudios = useSessionStore((s) => s.assignedStudios);
  const persona = useSessionStore((s) => s.persona);
  const agentId = useSessionStore((s) => s.agentId);
  const actorId = agentId ?? persona ?? 'unknown';

  const allowAll = persona === 'georgina' || persona === 'owner';
  const activeStudios = Object.values(studios).filter((s) => !s.archived);
  const [studioId, setStudioId] = useState<StudioId>(
    (allowAll ? undefined : assignedStudios[0]) ?? (activeStudios[0]?.id as StudioId),
  );
  const studio = studios[studioId] ?? activeStudios[0];

  const [lane, setLane] = useState<Lane>('makesale');
  const [dayFilter, setDayFilter] = useState<string>('all');
  const [showMatched, setShowMatched] = useState(false);
  const [resolving, setResolving] = useState<{ rowId: string; reference: string; lane: Lane } | null>(null);

  const days = useMemo(
    () => reconciliationDaysFor(studio?.id as StudioId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studio?.id, salePayments, docketLines, cashLedgers],
  );

  const sale = useMemo(
    () => makeSaleLane(studio!.id, clockIso),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studio?.id, clockIso, salePayments, docketLines, settlementLumps],
  );
  const dep = useMemo(
    () => reconcileDeposits(studio!.id, clockIso),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studio?.id, clockIso, deposits, depositLandings],
  );

  const inDay = (d: string) => dayFilter === 'all' || d === dayFilter;
  const msNeeds = sale.needsYou.filter((r) => inDay(r.dayIso));
  const msMatched = sale.matched.filter((r) => inDay(r.dayIso));
  const msAwait = sale.awaitingBank.filter((r) => inDay(r.dayIso));
  const depNeeds = dep.needsYou.filter((r) => inDay(r.dayIso));
  const depMatched = dep.matched.filter((r) => inDay(r.dayIso));
  const depAwait = dep.awaiting.filter((r) => inDay(r.dayIso));

  // Resolution overlay (K1): an exception row splits by its resolution state -
  // still open, parked awaiting approval, or approved (cleared into matched).
  const splitByResolution = <T extends { id: string }>(rows: T[]) => ({
    open: rows.filter((r) => classifyResolvedRow(r.id, reconResolutions) === 'needs'),
    awaiting: rows.filter((r) => classifyResolvedRow(r.id, reconResolutions) === 'awaiting'),
    approved: rows.filter((r) => classifyResolvedRow(r.id, reconResolutions) === 'approved'),
  });
  const msSplit = splitByResolution(msNeeds);
  const depSplit = splitByResolution(depNeeds);
  const depPartials = partialTopUps(dep).filter((p) => inDay(p.movements[0]?.dayIso ?? ''));

  // An approved exception is cleared; only open + awaiting-approval rows still need work.
  const laneNeeds = {
    makesale: msSplit.open.length + msSplit.awaiting.length,
    deposit: depSplit.open.length + depSplit.awaiting.length,
  };
  const totalNeeds = laneNeeds.makesale + laneNeeds.deposit;

  const openResolve = (rowId: string, reference: string, forLane: Lane) =>
    setResolving({ rowId, reference, lane: forLane });
  const saveResolve = (note: string, attachment?: ReconAttachment) => {
    if (!resolving) return;
    resolveReconRow({ rowId: resolving.rowId, lane: resolving.lane, reference: resolving.reference, note, attachment });
    setResolving(null);
  };

  const till = dayFilter === 'all' ? null : reconcileDay(studio!.id, dayFilter).cash;

  return (
    <section className="px-8 py-8" data-testid="reconciliation">
      <header className="flex items-end justify-between max-w-7xl flex-wrap gap-4">
        <div>
          <p className="label-small-caps text-gold-bright">Money · Back office</p>
          <h1 className="heading-display text-3xl text-cream mt-2 inline-flex items-center gap-2">
            Reconcile
            <InfoTip label="What is reconciliation">
              The back-office check that every sale and deposit actually reached the bank. Two reconciliations in one
              place: make-sale (till takings vs the terminal + bank) and deposit (deposit sheet vs the bank). Lines
              auto-match; you only work the handful that didn&apos;t — the exceptions.
            </InfoTip>
          </h1>
          <p className="font-serif text-cream-soft mt-3 max-w-2xl">
            The two reconciliations, in one place. Every line auto-matches against the terminal and the
            bank &mdash; you only ever work the handful that didn&rsquo;t.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="font-inter text-[10px] uppercase tracking-widest text-cream-soft">Studio</label>
          <select
            value={studio?.id ?? ''}
            onChange={(e) => {
              setStudioId(e.target.value as StudioId);
              setDayFilter('all');
            }}
            className="bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-1.5 text-sm text-cream"
            data-testid="reconciliation-studio-filter"
          >
            {activeStudios.map((s) => (
              <option key={s.id} value={s.id}>
                {s.brand} · {s.name}
              </option>
            ))}
          </select>
        </div>
      </header>

      {/* Control bar: day filter + overall status */}
      <div className="mt-6 flex items-center justify-between flex-wrap gap-3 max-w-7xl">
        <div className="flex flex-wrap items-center gap-2" data-testid="reconciliation-day-picker">
          <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint mr-1">Day</span>
          <DayChip label="Recent · all" active={dayFilter === 'all'} onClick={() => setDayFilter('all')} />
          {days.map((d) => {
            const st = reconcileDay(studio!.id, d).status;
            const dot = st === 'balanced' ? 'bg-mint' : st === 'pending-count' ? 'bg-gold-bright' : 'bg-blood';
            return (
              <DayChip
                key={d}
                label={dayLabel(d)}
                dot={dot}
                active={dayFilter === d}
                onClick={() => setDayFilter(d)}
                testid={`recon-day-${d}`}
              />
            );
          })}
        </div>
        <span
          className={clsx(
            'inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold border',
            totalNeeds === 0
              ? 'text-mint bg-mint/10 border-mint/45'
              : 'text-blood bg-blood/10 border-blood/45',
          )}
          data-testid="reconciliation-status"
        >
          {totalNeeds === 0 ? 'Everything ties out' : `${totalNeeds} to clear`}
        </span>
      </div>

      {/* Lane tabs */}
      <div className="mt-6 flex gap-1 border-b border-gold-dim/40 max-w-7xl">
        <LaneTab
          label="Make-sale reconciliation"
          count={laneNeeds.makesale}
          active={lane === 'makesale'}
          onClick={() => setLane('makesale')}
          testid="recon-tab-makesale"
        />
        <LaneTab
          label="Deposit reconciliation"
          count={laneNeeds.deposit}
          active={lane === 'deposit'}
          onClick={() => setLane('deposit')}
          testid="recon-tab-deposit"
        />
      </div>

      {lane === 'makesale' ? (
        <div className="mt-6 max-w-7xl" data-testid="recon-lane-makesale">
          <SummaryRow needs={msSplit.open.length} matchedN={msMatched.length} matchedLabel="matched" awaitN={msAwait.length} />

          {/* Needs you */}
          <Block
            tone="alert"
            title="Needs you"
            count={msSplit.open.length}
            info="Sale lines that did not auto-match: no docket from the terminal, an amount that disagrees, or a missing name. These are the only ones you work by hand."
          >
            {msSplit.open.length === 0 ? (
              <Empty>Nothing to chase &mdash; every line matched.</Empty>
            ) : (
              <MakeSaleTable rows={msSplit.open} showWrong onResolve={(r) => openResolve(r.id, r.reference, 'makesale')} resolutions={reconResolutions} />
            )}
          </Block>

          {/* Awaiting approval (K1) */}
          {msSplit.awaiting.length > 0 ? (
            <ApprovalBlock
              rows={msSplit.awaiting}
              resolutions={reconResolutions}
              persona={persona}
              actorId={actorId}
              onApprove={approveReconResolution}
              onReject={rejectReconResolution}
            />
          ) : null}

          {msSplit.approved.length > 0 ? (
            <ClearedList rows={msSplit.approved} resolutions={reconResolutions} />
          ) : null}

          {/* Matched (collapsible) */}
          <Block
            title="Matched"
            tone="plain"
            count={msMatched.length}
            headright={
              <button
                type="button"
                onClick={() => setShowMatched((v) => !v)}
                className="font-inter text-[11px] uppercase tracking-widest text-cream-faint hover:text-cream-soft"
              >
                {showMatched ? 'hide' : 'show'} · {aud(msMatched.reduce((s, r) => s + r.movedAud, 0))}
              </button>
            }
          >
            {msMatched.length === 0 ? (
              <Empty>No matched make-sales for this day.</Empty>
            ) : showMatched ? (
              <MakeSaleTable rows={msMatched} />
            ) : (
              <p className="font-serif text-cream-soft text-sm py-1">
                {msMatched.length} line{msMatched.length === 1 ? '' : 's'} tied themselves off. Recorded, EFTPOS echo
                and bank-landed show on each when expanded.
              </p>
            )}
          </Block>

          {/* Awaiting bank */}
          <Block
            title="Awaiting bank"
            tone="await"
            count={msAwait.length}
            info="Matched sales whose card takings have not settled into the bank yet. Card settles 24-36h later and never over the weekend, so these are normal in flight — only overdue ones climb into Needs you."
          >
            {msAwait.length === 0 ? (
              <Empty>Nothing in flight for this day.</Empty>
            ) : (
              <>
                <MakeSaleTable rows={msAwait} />
                <p className="font-inter text-[11px] text-cream-faint mt-3">
                  Card takings settle 24&ndash;36h later and nothing lands over the weekend, so recent sales with no
                  echo are expected &mdash; only the overdue ones climb into Needs you.
                </p>
              </>
            )}
          </Block>

          {/* Till strip (read-only, from Payouts & cash) */}
          <div
            className="mt-2 flex items-center justify-between gap-4 flex-wrap rounded-md border border-dashed border-gold-dim/55 bg-ink-soft/40 px-4 py-3"
            data-testid="reconciliation-till"
          >
            {till ? (
              <>
                <p className="font-serif text-sm text-cream-soft">
                  <span className="text-cream font-semibold">Till close</span> · {dayLabel(dayFilter)} · counted on
                  Payouts &amp; cash
                </p>
                <div className="flex items-center gap-5 font-mono tabular-nums text-sm">
                  <span className="text-cream-soft">
                    expected <span className="text-cream font-bold">{aud(till.expectedAud)}</span>
                  </span>
                  <span className="text-cream-soft">
                    counted{' '}
                    <span className="text-cream font-bold">
                      {till.counted ? aud(till.countedAud ?? 0) : '— pending'}
                    </span>
                  </span>
                  {till.counted ? (
                    <span
                      className={clsx('font-bold', till.varianceAud === 0 ? 'text-mint' : 'text-blood')}
                    >
                      {till.varianceAud === 0
                        ? 'balanced'
                        : `${aud(Math.abs(till.varianceAud))} ${till.varianceAud > 0 ? 'short' : 'over'}`}
                    </span>
                  ) : null}
                </div>
              </>
            ) : (
              <p className="font-serif text-sm text-cream-soft">
                <span className="text-cream font-semibold">Till close</span> is counted on Payouts &amp; cash at day
                close. Pick a day to see its count.
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-6 max-w-7xl" data-testid="recon-lane-deposit">
          <SummaryRow
            needs={depSplit.open.length}
            matchedN={depMatched.length}
            matchedLabel="matched · bank = system"
            awaitN={depAwait.length}
          />

          {/* Needs you — the two failure modes */}
          <Block
            tone="alert"
            title="Needs you"
            count={depSplit.open.length}
            info="Deposits where the bank and the deposit sheet disagree — the two failure modes: money in the bank with no sheet entry, or a sheet entry the bank never received."
          >
            {depSplit.open.length === 0 ? (
              <Empty>Nothing to chase &mdash; every deposit tied to the bank.</Empty>
            ) : (
              <DepositTable rows={depSplit.open} showWrong onResolve={(r) => openResolve(r.id, r.reference, 'deposit')} resolutions={reconResolutions} />
            )}
          </Block>

          {/* Awaiting approval (K1) */}
          {depSplit.awaiting.length > 0 ? (
            <ApprovalBlock
              rows={depSplit.awaiting}
              resolutions={reconResolutions}
              persona={persona}
              actorId={actorId}
              onApprove={approveReconResolution}
              onReject={rejectReconResolution}
            />
          ) : null}

          {depSplit.approved.length > 0 ? (
            <ClearedList rows={depSplit.approved} resolutions={reconResolutions} />
          ) : null}

          {/* Partial — awaiting top-up (K1): a deposit paid in parts, never an exception */}
          {depPartials.length > 0 ? <PartialBlock partials={depPartials} /> : null}

          {/* Matched (collapsible) */}
          <Block
            title="Matched"
            tone="plain"
            count={depMatched.length}
            headright={
              <button
                type="button"
                onClick={() => setShowMatched((v) => !v)}
                className="font-inter text-[11px] uppercase tracking-widest text-cream-faint hover:text-cream-soft"
              >
                {showMatched ? 'hide' : 'show'} · {aud(depMatched.reduce((s, r) => s + r.movedAud, 0))}
              </button>
            }
          >
            {depMatched.length === 0 ? (
              <Empty>No matched deposits for this day.</Empty>
            ) : showMatched ? (
              <DepositTable rows={depMatched} showBank />
            ) : (
              <p className="font-serif text-cream-soft text-sm py-1">
                {depMatched.length} deposit{depMatched.length === 1 ? '' : 's'} tied bank = system (cash sits in the
                till).
              </p>
            )}
          </Block>

          {/* Awaiting bank */}
          <Block title="Awaiting bank" tone="await" count={depAwait.length}>
            {depAwait.length === 0 ? (
              <Empty>Nothing in flight for this day.</Empty>
            ) : (
              <>
                <DepositTable rows={depAwait} showBank />
                <p className="font-inter text-[11px] text-cream-faint mt-3">
                  Deposits taken recently; the bank echoes them 24&ndash;36h later. Normal &mdash; they only need
                  chasing once overdue.
                </p>
              </>
            )}
          </Block>
        </div>
      )}

      {resolving ? (
        <ResolveModal reference={resolving.reference} onCancel={() => setResolving(null)} onSave={saveResolve} />
      ) : null}
    </section>
  );
}

// --- small presentational helpers -----------------------------------------

function DayChip({
  label,
  active,
  onClick,
  dot,
  testid,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  dot?: string;
  testid?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={clsx(
        'rounded-full border px-3.5 py-1.5 text-xs font-inter transition inline-flex items-center gap-2',
        active
          ? 'bg-gold-bright text-ink-black border-gold-bright font-semibold'
          : 'border-gold-dim/50 text-cream-soft hover:bg-ink-soft/60',
      )}
    >
      {label}
      {dot ? <span className={clsx('inline-block h-1.5 w-1.5 rounded-full', active ? 'bg-ink-black/50' : dot)} /> : null}
    </button>
  );
}

function LaneTab({
  label,
  count,
  active,
  onClick,
  testid,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  testid: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={clsx(
        'px-4 py-3 text-sm font-semibold -mb-px border-b-2 inline-flex items-center gap-2 transition',
        active
          ? 'text-cream border-gold-bright'
          : 'text-cream-faint border-transparent hover:text-cream-soft',
      )}
    >
      {label}
      <span
        className={clsx(
          'font-mono text-[11px] font-bold rounded-full px-2 py-0.5',
          count === 0 ? 'bg-mint/15 text-mint' : 'bg-blood/15 text-blood',
        )}
      >
        {count}
      </span>
    </button>
  );
}

function SummaryRow({
  needs,
  matchedN,
  matchedLabel,
  awaitN,
}: {
  needs: number;
  matchedN: number;
  matchedLabel: string;
  awaitN: number;
}) {
  return (
    <div className="flex flex-wrap gap-2.5 mb-5">
      <Schip n={needs} label="needs you" alert />
      <Schip n={matchedN} label={matchedLabel} />
      <Schip n={awaitN} label="awaiting bank" />
    </div>
  );
}

function Schip({ n, label, alert }: { n: number; label: string; alert?: boolean }) {
  return (
    <span className="inline-flex items-baseline gap-2 rounded-lg border border-gold-dim/35 bg-ink-soft/40 px-3.5 py-2 text-sm text-cream-soft">
      <b className={clsx('font-mono text-base', alert && n > 0 ? 'text-blood' : 'text-cream')}>{n}</b>
      {label}
    </span>
  );
}

function Block({
  title,
  count,
  tone,
  headright,
  info,
  testid,
  children,
}: {
  title: string;
  count: number;
  tone: 'alert' | 'await' | 'plain' | 'approve' | 'partial';
  headright?: React.ReactNode;
  info?: React.ReactNode;
  testid?: string;
  children: React.ReactNode;
}) {
  const titleColour =
    tone === 'alert' ? 'text-blood' : tone === 'await' ? 'text-azure' : 'text-gold-bright';
  const countColour =
    tone === 'alert'
      ? 'bg-blood text-ink-black'
      : tone === 'await'
        ? 'bg-azure text-ink-black'
        : tone === 'plain'
          ? 'bg-cream-soft/70 text-ink-black'
          : 'bg-gold-bright text-ink-black';
  return (
    <div
      data-testid={testid}
      className={clsx(
        'panel p-4 mb-4',
        tone === 'alert' ? 'border-blood/40 bg-blood/5' : '',
        tone === 'approve' ? 'border-gold-bright/50 bg-gold-bright/5' : '',
        tone === 'partial' ? 'border-gold-dim/60 border-dashed bg-ink-soft/30' : '',
      )}
    >
      <div className="flex items-center gap-3 mb-3">
        <p className={clsx('label-small-caps inline-flex items-center gap-1', titleColour)}>
          {title}
          {info ? <InfoTip>{info}</InfoTip> : null}
        </p>
        <span className={clsx('font-mono text-[11px] font-bold rounded-full px-2 py-0.5', countColour)}>{count}</span>
        {headright ? <div className="ml-auto">{headright}</div> : null}
      </div>
      {children}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="font-serif text-cream-soft text-sm">{children}</p>;
}

/**
 * A sale's bank status with its checkable settlement trail: cash sits in the till;
 * a card sale shows when its lump landed (or is due) plus the batch + terminal
 * references the office ticks against the bank statement.
 */
function BankCell({ row }: { row: MakeSaleRow }) {
  const m = BANK_STAGE_META[row.bankStage];
  const s = row.settlement;
  const label =
    s && row.bankStage === 'landed'
      ? `Landed ${dayLabel(s.landedOn)}`
      : s && row.bankStage === 'awaiting'
        ? `Awaiting · expected ${dayLabel(s.landedOn)}`
        : m.label;
  return (
    <span className="inline-flex flex-col gap-0.5">
      <span className="inline-flex items-center gap-2">
        <span className={clsx('h-1.5 w-1.5 rounded-full', m.dot)} />
        <span className={clsx('text-[13px]', m.text)}>{label}</span>
      </span>
      {s ? (
        <span className="font-mono text-[11px] text-cream-faint whitespace-nowrap">
          {s.batchRef} · {s.terminal}
        </span>
      ) : null}
    </span>
  );
}

/**
 * L4: shows why a resolution was bounced back, on the row now sitting in Needs
 * you again. Renders nothing unless the row's resolution is `rejected`.
 */
function RejectedNote({ resolution, reference }: { resolution?: ReconResolution; reference: string }) {
  if (resolution?.status !== 'rejected' || !resolution.rejectionNote) return null;
  return (
    <p
      data-testid={`rejected-note-${reference}`}
      className="mt-1 text-[11.5px] text-gold-bright"
    >
      Rejected: {resolution.rejectionNote}
    </p>
  );
}

function MakeSaleTable({
  rows,
  showWrong,
  onResolve,
  resolutions,
}: {
  rows: MakeSaleRow[];
  showWrong?: boolean;
  onResolve?: (row: MakeSaleRow) => void;
  resolutions?: Record<string, ReconResolution>;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left">
            <Th>Date</Th>
            <Th>Ref</Th>
            <Th>Name</Th>
            {showWrong ? <Th>What&rsquo;s wrong</Th> : null}
            <Th right>Recorded</Th>
            <Th right>EFTPOS</Th>
            <Th right>Landed</Th>
            <Th>Bank</Th>
            {onResolve ? <Th right> </Th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-gold-dim/20" data-testid={`ms-row-${r.reference}`}>
              <Td className="text-cream-soft whitespace-nowrap text-[12.5px]">{dayLabel(r.dayIso)}</Td>
              <Td className="font-mono text-[12px] text-cream-soft whitespace-nowrap">{r.reference}</Td>
              <Td className="text-cream">{r.name}</Td>
              {showWrong ? (
                <Td>
                  {r.exception ? (
                    <span className="text-blood text-[12.5px]">
                      <span className="font-semibold">{EXCEPTION_LABEL[r.exception.kind]}</span> · {r.exception.detail}
                    </span>
                  ) : (
                    <span className="text-cream-faint">&mdash;</span>
                  )}
                  <RejectedNote resolution={resolutions?.[r.id]} reference={r.reference} />
                </Td>
              ) : null}
              <Td right className="font-mono tabular-nums text-cream font-semibold whitespace-nowrap">
                {aud(r.movedAud)}
              </Td>
              <Td right className="font-mono tabular-nums whitespace-nowrap">
                {r.docketAud != null ? (
                  <span className={r.docketAud !== r.movedAud ? 'text-blood font-semibold' : 'text-cream-soft'}>
                    {aud(r.docketAud)}
                  </span>
                ) : (
                  <span className="text-cream-faint">&mdash;</span>
                )}
              </Td>
              <Td right className="font-mono tabular-nums whitespace-nowrap text-cream-soft">
                {r.settlement ? aud(r.settlement.amountAud) : <span className="text-cream-faint">&mdash;</span>}
              </Td>
              <Td>
                <BankCell row={r} />
              </Td>
              {onResolve ? (
                <Td right>
                  <ResolveButton onClick={() => onResolve(r)} reference={r.reference} />
                </Td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * A deposit's reconciliation state with the landing date folded in — so a matched
 * bank deposit shows when its money actually landed, and a cash one reads "in till".
 */
function DepositStateChip({ row }: { row: DepositReconRow }) {
  const m = DEPOSIT_STATE_META[row.state];
  const label =
    row.state === 'matched'
      ? row.landedOn
        ? `Matched · landed ${dayLabel(row.landedOn)}`
        : row.channel === 'cash'
          ? 'Matched · in till'
          : m.label
      : m.label;
  return (
    <span className="inline-flex items-center gap-2">
      <span className={clsx('h-1.5 w-1.5 rounded-full', m.dot)} />
      <span className={clsx('text-[13px]', m.text)}>{label}</span>
      {row.shortfallAud > 0 ? (
        <span className="text-[12px] text-gold-bright">· short {aud(row.shortfallAud)}</span>
      ) : null}
    </span>
  );
}

function DepositTable({
  rows,
  showWrong,
  showBank,
  onResolve,
  resolutions,
}: {
  rows: DepositReconRow[];
  showWrong?: boolean;
  showBank?: boolean;
  onResolve?: (row: DepositReconRow) => void;
  resolutions?: Record<string, ReconResolution>;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left">
            <Th>Date</Th>
            <Th>Ref</Th>
            <Th>Name</Th>
            <Th>Channel</Th>
            <Th>Kind</Th>
            {showWrong ? <Th>What&rsquo;s wrong</Th> : null}
            <Th right>Sent</Th>
            <Th right>Paid</Th>
            <Th right>Landed</Th>
            {showBank ? <Th>State</Th> : null}
            {onResolve ? <Th right> </Th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            // An orphan bank landing has no system side; everything else is a money movement.
            const orphan = r.state === 'in-bank-not-system';
            const dash = <span className="text-cream-faint">&mdash;</span>;
            return (
              <tr key={r.id} className="border-t border-gold-dim/20" data-testid={`dep-row-${r.reference}`}>
                <Td className="text-cream-soft whitespace-nowrap text-[12.5px]">{dayLabel(r.dayIso)}</Td>
                <Td className="font-mono text-[12px] text-cream-soft whitespace-nowrap">{r.reference}</Td>
                <Td className="text-cream">{r.name}</Td>
                <Td className="text-cream-faint text-[11px] uppercase tracking-wide">{CHANNEL_LABEL[r.channel]}</Td>
                <Td className="text-cream-faint text-[11px] uppercase tracking-wide">{orphan ? dash : r.movementKind}</Td>
                {showWrong ? (
                  <Td className="text-blood text-[12.5px]">
                    <span className="font-semibold">{DEPOSIT_STATE_META[r.state].label}</span>
                    {r.detail ? ` · ${r.detail}` : ''}
                    <RejectedNote resolution={resolutions?.[r.id]} reference={r.reference} />
                  </Td>
                ) : null}
                <Td right className="font-mono tabular-nums whitespace-nowrap text-cream-faint">
                  {orphan ? dash : aud(r.sentAud)}
                </Td>
                <Td right className="font-mono tabular-nums text-cream font-semibold whitespace-nowrap">
                  {orphan ? dash : aud(r.movedAud)}
                </Td>
                <Td right className="font-mono tabular-nums whitespace-nowrap text-cream-soft">
                  {orphan || r.landedOn ? (
                    aud(r.movedAud)
                  ) : r.channel === 'cash' && r.state === 'matched' ? (
                    <span className="text-[12px] text-cream-soft">in till</span>
                  ) : (
                    dash
                  )}
                </Td>
                {showBank ? (
                  <Td>
                    <DepositStateChip row={r} />
                  </Td>
                ) : null}
                {onResolve ? (
                  <Td right>
                    <ResolveButton onClick={() => onResolve(r)} reference={r.reference} />
                  </Td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <th
      className={clsx(
        'font-inter text-[9px] uppercase tracking-widest text-cream-faint pb-2 pr-3',
        right ? 'text-right' : '',
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, right, className }: { children: React.ReactNode; right?: boolean; className?: string }) {
  return <td className={clsx('py-2 pr-3', right ? 'text-right' : '', className)}>{children}</td>;
}

// --- K1: resolve + approval + partial-top-up ------------------------------

function ResolveButton({ onClick, reference }: { onClick: () => void; reference: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`resolve-${reference}`}
      className="rounded-md border border-gold-bright/50 bg-gold-bright/10 px-3 py-1.5 text-[12.5px] font-semibold text-gold-bright hover:bg-gold-bright/20 transition whitespace-nowrap"
    >
      Resolve
    </button>
  );
}

/** Both lanes' rows share this shape for the approval + cleared views. */
interface ApprovableRow {
  id: string;
  reference: string;
  name: string;
  dayIso: string;
}

function ApprovalBlock({
  rows,
  resolutions,
  persona,
  actorId,
  onApprove,
  onReject,
}: {
  rows: ApprovableRow[];
  resolutions: Record<string, ReconResolution>;
  persona: Persona | null;
  actorId: string;
  onApprove: (rowId: string) => void;
  onReject: (rowId: string, note: string) => void;
}) {
  return (
    <Block
      tone="approve"
      title="Awaiting approval"
      count={rows.length}
      testid="recon-awaiting-approval"
      info="Exceptions someone investigated and resolved with a note. The Finance Manager, a different person, signs each off into Matched — you cannot approve your own. Rejecting one needs a reason, which lands back in Needs you."
    >
      <div className="space-y-3">
        {rows.map((r) => {
          const res = resolutions[r.id];
          if (!res) return null;
          const block = reconApprovalBlock(persona, res, actorId);
          return (
            <ApprovalRow
              key={r.id}
              row={r}
              res={res}
              canApprove={block.canApprove}
              blockedReason={block.canApprove ? undefined : block.reason}
              onApprove={onApprove}
              onReject={onReject}
            />
          );
        })}
      </div>
    </Block>
  );
}

function ApprovalRow({
  row: r,
  res,
  canApprove,
  blockedReason,
  onApprove,
  onReject,
}: {
  row: ApprovableRow;
  res: ReconResolution;
  canApprove: boolean;
  blockedReason?: ApprovalBlockReason;
  onApprove: (rowId: string) => void;
  onReject: (rowId: string, note: string) => void;
}) {
  const [rejecting, setRejecting] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  return (
    <div
      data-testid={`approval-${r.reference}`}
      className="rounded-lg border border-gold-dim/30 bg-ink-soft/40 p-3"
    >
      <p className="font-inter text-[12.5px] text-cream">
        <span className="font-mono text-cream-soft">{r.reference}</span> · {r.name}
        <span className="text-cream-faint"> · {dayLabel(r.dayIso)}</span>
      </p>
      <p className="font-inter text-[11px] text-cream-faint mt-1">
        Resolved by <span className="text-cream">{res.resolvedByName}</span> ·{' '}
        {dayLabel(res.resolvedAt.slice(0, 10))}
      </p>
      <p className="font-serif text-sm text-cream mt-1.5 pl-3 border-l-2 border-gold-dim/50">
        {res.note}
      </p>
      {res.attachment ? (
        <span className="inline-flex items-center gap-2 mt-2 text-[12px] text-cream-soft bg-ink-deep border border-gold-dim/30 rounded px-2 py-1">
          📎 {res.attachment.filename}{' '}
          <span className="text-cream-faint font-mono text-[11px]">{res.attachment.sizeLabel}</span>
        </span>
      ) : null}
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <button
          type="button"
          disabled={!canApprove}
          onClick={() => onApprove(r.id)}
          data-testid={`approve-${r.reference}`}
          className={clsx(
            'rounded-md px-3 py-1.5 text-[12.5px] font-semibold transition',
            canApprove
              ? 'bg-gold-bright text-ink-black hover:bg-gold-bright/90'
              : 'bg-ink-soft/60 text-cream-faint cursor-not-allowed',
          )}
        >
          Approve
        </button>
        <button
          type="button"
          onClick={() => setRejecting((v) => !v)}
          data-testid={`reject-${r.reference}`}
          className="text-blood text-[12px] font-semibold px-2 py-1.5 hover:underline"
        >
          Reject
        </button>
        {!canApprove ? (
          <span data-testid={`approve-blocked-${r.reference}`} className="text-[11.5px] text-blood">
            {blockedReason === 'self-approval'
              ? 'You resolved this — a different person must approve.'
              : 'Only the Finance Manager can sign this off.'}
          </span>
        ) : null}
      </div>
      {rejecting ? (
        <div className="mt-3 rounded-md border border-blood/40 bg-blood/5 p-3" data-testid={`reject-gate-${r.reference}`}>
          <label
            htmlFor={`reject-note-${r.reference}`}
            className="font-inter text-[11px] uppercase tracking-widest text-blood block mb-1"
          >
            Reason for rejecting <span aria-hidden="true">*</span>
          </label>
          <textarea
            id={`reject-note-${r.reference}`}
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            rows={2}
            placeholder="Why is this going back? Required…"
            className="w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream font-serif focus:outline-none focus:border-blood"
            data-testid={`reject-note-${r.reference}`}
          />
          <button
            type="button"
            disabled={!rejectNote.trim()}
            onClick={() => onReject(r.id, rejectNote)}
            data-testid={`reject-confirm-${r.reference}`}
            className="mt-2 rounded-md px-3 py-1.5 text-[12.5px] font-semibold bg-blood text-cream hover:bg-blood/90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Confirm reject
          </button>
        </div>
      ) : null}
    </div>
  );
}

function ClearedList({
  rows,
  resolutions,
}: {
  rows: ApprovableRow[];
  resolutions: Record<string, ReconResolution>;
}) {
  return (
    <Block tone="plain" title="Cleared this session" count={rows.length} testid="recon-cleared">
      <div className="space-y-1.5">
        {rows.map((r) => {
          const res = resolutions[r.id];
          if (!res) return null;
          return (
            <p
              key={r.id}
              data-testid={`cleared-${r.reference}`}
              className="font-inter text-[12.5px] text-cream-soft"
            >
              <span className="font-mono text-cream">{r.reference}</span> · {r.name} —{' '}
              <span className="text-cream-faint">
                resolved by {res.resolvedByName}, approved by {res.approvedByName}
              </span>
              <span className="text-mint text-[11px]"> · into matched</span>
            </p>
          );
        })}
      </div>
    </Block>
  );
}

function PartialBlock({ partials }: { partials: PartialTopUp[] }) {
  return (
    <Block
      tone="partial"
      title="Partial — awaiting top-up"
      count={partials.length}
      testid="recon-partial"
      info="Deposits paid in parts: the sent amount has not all arrived yet. A top-up to chase, not a bank mismatch — each movement still reconciles on its own."
    >
      <div className="space-y-4">
        {partials.map((p) => (
          <div key={p.reference} data-testid={`partial-${p.reference}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="font-inter text-[13px] text-cream">
                <span className="font-mono text-cream-soft">{p.reference}</span> · {p.name}
                <span className="ml-2 inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-gold-bright bg-gold-bright/10 border border-gold-bright/35 rounded-full px-2.5 py-0.5">
                  partial — awaiting top-up
                </span>
              </p>
              <p className="font-mono tabular-nums text-[12.5px] text-cream-soft">
                sent <span className="text-cream">{aud(p.sentAud)}</span> · paid{' '}
                <span className="text-cream">{aud(p.paidAud)}</span> ·{' '}
                <span className="text-gold-bright">{aud(p.outstandingAud)} outstanding</span>
              </p>
            </div>
            <div className="mt-2 pl-3 border-l-2 border-gold-dim/50 space-y-1.5">
              {p.movements.map((m, i) => (
                <p key={i} className="font-inter text-[12.5px] text-cream-soft flex items-center gap-2 flex-wrap">
                  <span
                    className={clsx(
                      'h-1.5 w-1.5 rounded-full',
                      m.state === 'matched' ? 'bg-mint' : m.state === 'awaiting' ? 'bg-azure' : 'bg-blood',
                    )}
                  />
                  <span className="font-mono tabular-nums text-cream">{aud(m.movedAud)}</span>
                  <span className="text-cream-faint">
                    {m.movementKind} · {dayLabel(m.dayIso)}
                  </span>
                  {m.landedOn ? (
                    <span className="text-mint text-[11px]">landed {dayLabel(m.landedOn)}</span>
                  ) : (
                    <span className="text-azure text-[11px]">{m.state}</span>
                  )}
                </p>
              ))}
              <p className="font-inter text-[12px] text-gold-bright flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-gold-dim" />
                <span className="font-mono tabular-nums">{aud(p.outstandingAud)}</span>
                <span className="text-cream-faint">top-up · awaiting — matches here when it lands</span>
              </p>
            </div>
          </div>
        ))}
      </div>
    </Block>
  );
}

function ResolveModal({
  reference,
  onCancel,
  onSave,
}: {
  reference: string;
  onCancel: () => void;
  onSave: (note: string, attachment?: ReconAttachment) => void;
}) {
  const [note, setNote] = useState('');
  const [attached, setAttached] = useState(false);
  const attachment: ReconAttachment = { filename: 'bank-statement.pdf', sizeLabel: '84 KB' };
  const canSave = note.trim().length > 0;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Resolve exception"
      className="fixed inset-0 z-50 flex items-center justify-center p-5 bg-ink-black/60"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="panel p-5 w-full max-w-md" data-testid="resolve-modal">
        <h3 className="heading-serif text-lg text-cream">Resolve exception</h3>
        <p className="font-mono text-[12px] text-cream-soft mt-1">{reference}</p>
        <label htmlFor="resolve-note" className="label-small-caps text-cream-faint block mt-4 mb-1.5">
          Resolution note <span className="text-blood">required</span>
        </label>
        <textarea
          id="resolve-note"
          data-testid="resolve-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What did you find, and how did you clear it?"
          className="w-full min-h-[80px] rounded-md bg-ink-deep border border-gold-dim/40 px-3 py-2 text-sm text-cream"
        />
        <p className="font-inter text-[11px] text-cream-faint mt-1.5">
          Saved with your name and the time. A different manager approves it — you cannot approve your own.
        </p>
        <div className="mt-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => setAttached(true)}
            className="rounded-md border border-gold-dim/40 px-3 py-1.5 text-[12.5px] text-cream-soft hover:bg-ink-soft/60"
          >
            📎 Attach file
          </button>
          {attached ? (
            <span className="inline-flex items-center gap-2 text-[12px] text-cream-soft">
              📎 {attachment.filename}{' '}
              <span className="text-cream-faint font-mono text-[11px]">{attachment.sizeLabel}</span>
            </span>
          ) : null}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-gold-dim/40 px-3 py-1.5 text-[12.5px] text-cream-soft"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSave}
            data-testid="resolve-save"
            onClick={() => onSave(note, attached ? attachment : undefined)}
            className={clsx(
              'rounded-md px-3 py-1.5 text-[12.5px] font-semibold',
              canSave ? 'bg-gold-bright text-ink-black' : 'bg-ink-soft/60 text-cream-faint cursor-not-allowed',
            )}
          >
            Save — send for approval
          </button>
        </div>
      </div>
    </div>
  );
}
